import { AssociateAuditTrailService, AtAlertService, CountMetric, EnvironmentDetail, InterruptService, LatencyMetric } from '@amzn/ihm-associate-tools-js-lib'
import { CrossFrameUtils } from '@amzn/jwo-store-management-platform-components'
import { HiveServiceClient } from '@api/HiveServiceClient'
import {
    getStoreInfo,
} from '@atredux/selectors/storeSelector'
import { store } from '@atredux/store'
import { getToolById } from '@crossframe/apis/ToolRegistration'
import { getSelectedTool } from '@src/redux/selectors/toolSelector'
import { HeliosInterruptNotificationConstants, HeliosInterruptNotificationStates } from '@utils/Constants/HeliosInterruptNotificationStatesConstants'
import _ from 'lodash'

import { getNotifications } from './notifications/NotificationTaskService'
import { StoreFamily } from './StoreService'

const heliosTools = ['fetch_pick', 'swift_outbound_pick_tool', 'swift_outbound_pick_tool_v2', 'handoff_tool', 'handoff_pick_to_stage', 'checkin_tool',
    'batch_tool', 'batch_tool_redirect', 'load_tool', 'inventory_viewer_v2', 'unified_move_tool', 'unified_stock_tool']
const heliosStoreFamilies = [StoreFamily.HOUDINI, StoreFamily.MENDEL, StoreFamily.JANE]

let interruptNotification: any
const triggeredNotifications: any = []
let snoozedNotification: any
let snoozeTimer: any
let isToolRegistryLoaded = false
const rejectCallback: any = null
// const { t } = useTranslation();

const messages = {
    invalidNotification: 'notification is invalid.',
    reassignedMove: 'Your move was re-assigned to somebody else. Thanks anyway!',
    error: 'An error has occurred.',
    notificationExpired: 'Notification has expired. It\'s been over 30 seconds, Helios will try another associate.',
    instructions1: 'Please move to ',
    instructions2: ' and start working in ',
    unableToWait: 'Helios is unable to wait.',
}

// latency metric for interrupt modal rendered.
// For now, we want to record the latency from the modal rendered on device to associates reacts to it, no matter they click Yes or Snnoze.
const heliosModalResponseLatencyMetric = new LatencyMetric(undefined, 'Time', 'IhmFlyingDutchman', 'heliosInterruptService.heliosModalResponse')

/**
 * Initializes service, sets some store-specific variables.
 * Initialize it before NotificationTaskService.
 */
export const init = (storeId: string) => {
    // NOTE: unsure if the string translation ever needs to be re-enabled so leaving this init method here in case
    // translateMessages()
}

/**
 * Retrieves the currently snoozed notification, if any.
 */
export const getSnoozedNotification = () => {
    return snoozedNotification
}

/**
 * Sets whether the tool registry is loaded yet or not
 */
export const setIsToolRegistryLoaded = (flag: boolean) => {
    console.log('ATBase: setting isToolRegistryLoaded in HeliosInterruptService to: ', flag)
    isToolRegistryLoaded = flag
}

/**
 * Searches device notifications for a HELIOS-type to interrupt on. Checks that the notification hasn't already interrupted before, and adds to list of interrupted notifs.
 * Should only search for interrupt notifications when shouldSearchInterrupt (see below) is true OR method is called with forceInterrupt.
 *
 * @param {boolean} forceInterrupt - optional boolean to override the result of shouldSearchInterrupt
 * @return {object} The interrupt notification, if any, and if it passed triggerInterrupt validation and displayed a modal.
 */
export const searchInterruptNotifications = (forceInterrupt?: boolean): object => {
    if (!shouldSearchInterrupt() && !forceInterrupt) {
        return
    }
    if (!isToolRegistryLoaded) {
        console.warn('ATBase: skipping helios interrupt check as tool registry is not yet loaded')
        return
    }
    const currentNotifications = getNotifications()
    interruptNotification = currentNotifications.find((notification) => _.includes(HeliosInterruptNotificationConstants.NOTIFICATION_TYPES, notification.notificationType))

    if (!interruptNotification) {
        console.warn('ATBase: interruptNotification is undefined, returning without doing anything')
        return
    }
    console.debug('ATBase: interruptNotification taskId:taskType', interruptNotification.notificationId + ':' + interruptNotification.notificationType)
    if (alreadyInterrupted(interruptNotification)) {
        console.warn('ATBase: notification has already interrupted, skipping: ', interruptNotification)
        return
    }
    triggeredNotifications.push(interruptNotification)
    return triggerInterruptNotification(interruptNotification, false)
}

/**
 * Associate clicks snoozed banner to trigger interrupted notifications
 *
 * @param notification - The notification to interrupt on
 * @returns {object} - The notification that was used to call atInterrupt, if any.
 */
export const clickSnoozedNotificationBanner = (notification: any): object => {
    return triggerInterruptNotification(notification, true)
}

/**
 * TODO: fix translates messages
 */
const translateMessages = () => {
    /* if (!t) {
        return
    }
    messages = {
        invalidNotification: translate(t, 'migrated-ihm-seaward-helios-invalid-notification', 'No'),
        reassignedMove: translate(t, 'migrated-ihm-seaward-helios-reassigned', 'No'),
        error: translate(t, 'migrated-ihm-seaward-helios-error-message', 'No'),
        notificationExpired: translate(t, 'migrated-ihm-seaward-helios-expired-notification-alert', 'No'),
        instructions1: translate(t, 'migrated-ihm-seaward-helios-instructions-1', 'No'),
        instructions2: ' ' + translate(t, 'migrated-ihm-seaward-helios-instructions-2', 'No'),
        unableToWait: translate(t, 'migrated-ihm-seaward-helios-unable-to-wait-alert', 'No'),
    } */
}

/**
 * Only enable for mendel, houdini and Jane stores, and for tools that are not managed by Helios. These tools should not be eligible for hard-interrupts via background polling,
 * but will instead access labor moves via CrossFrame calls, which will pass in forceInterrupt as true.
 */
const shouldSearchInterrupt = () => {
    const currentTool: string = getSelectedTool(store.getState()).toolName
    const currentStoreFamily = getStoreInfo(store.getState())?.storeFamily
    return !currentTool || ((_.includes(heliosStoreFamilies, currentStoreFamily)) && !(_.includes(heliosTools, currentTool)))
}

/**
 * Notifications will be stored in triggeredNotifications after interrupting. Since getNotifications will pull the entire notification list (not DIFF),
 * perform a check to see if the notification has already interrupted in the same session.
 *
 * @param {any} interruptNotif: the notification to be searched against triggeredNotifications
 */
const alreadyInterrupted = (interruptNotif: any) => {
    return triggeredNotifications.find((triggeredNotification: any) => triggeredNotification.notificationId === interruptNotif.notificationId)
}

/**
 * Calls atInterrupt for a given notification.
 * Clears the snoozed notification and timer, if they exist.
 *
 * @param {any} notification - The notification to interrupt on.
 * @param {boolean} suppressAlerts - If the alert should be suppressed (e.g. associate clicking the snoozed banner)
 * @returns {object} The notification that was used to call atInterrupt, if any rejection if any error happens.
 */
export const triggerInterruptNotification = (notification: any, suppressAlerts: boolean): object => {
    const unsuccessfulMetric = new CountMetric(undefined, 'Unsuccessful', 'IhmFlyingDutchman', 'heliosInterruptService.triggerInterruptNotification')
    const faultMetric = new CountMetric(undefined, 'Fault', 'IhmFlyingDutchman', 'heliosInterruptService.triggerInterruptNotification')
    const heliosNotificationAssignLatencyMetric = new LatencyMetric(undefined, 'Time', 'IhmFlyingDutchman', 'heliosInterruptService.heliosNotificationAssign')

    const parsedNotification = parseNotification(notification)
    if (!isValidNotification(parsedNotification)) {
        console.warn('ATBase: HeliosInterruptService: Skipping invalid Helios notification', parsedNotification)
        return Promise.reject(messages.invalidNotification)
    }

    snoozedNotification = null
    clearTimeout(snoozeTimer)

    const interruptMessage = constructInterruptMessage(parsedNotification)
    if (!interruptMessage) {
        console.error('ATBase: heliosInterrupService could not construct interrupt message from notification: ' + parseNotification)
        return Promise.reject(messages.invalidNotification)
    }

    return HiveServiceClient.getInstance()
    .updateLaborMove(EnvironmentDetail.getStoreId(), parsedNotification.metadata.warehouseId, parsedNotification.metadata.laborMoveId, HeliosInterruptNotificationStates.ASSIGNED)
        .then((response) => {
            if (response.successful) {
                unsuccessfulMetric.count(0)
                const currentTime = (new Date()).getTime()
                const notificationGeneratedTime = (new Date(notification.updatedAt)).getTime()
                heliosNotificationAssignLatencyMetric.setValueAndBroadcast(currentTime - notificationGeneratedTime)
                emitLaborMoveAuditEvent(parsedNotification.metadata.laborMoveId, HeliosInterruptNotificationStates.ASSIGNED)
            } else {
                unsuccessfulMetric.count(1)
                if (suppressAlerts) {
                    console.debug('ATBase: HeliosInterruptService: updateLaborMove called by associate tapping snoozed banner is unsuccessful. Associate should be alerted.')
                    AtAlertService.alert(messages.reassignedMove, '')
                } else {
                    console.debug('ATBase: HeliosInterruptService: updateLaborMove called by system searching interrupt notification is unsuccessful. Associate shouldn\'t be alerted.')
                }
            }

            return response.successful
        })
        .then((successful) => {
            if (!successful) {
                console.debug('ATBase: HeliosInterruptService: received unsuccessful response on laborMove')
                return null
            }
            if (isAlreadyInDestinationTool(parsedNotification.applicableTool)) {
                console.debug('ATBase: HeliosInterruptService: Associate is already in destination tool:' + parsedNotification.applicableTool + '. Don\'t show notification modal.')
                return notification
            }
            heliosModalResponseLatencyMetric.start()
            InterruptService.interrupt(interruptMessage,
                () => {
                    yesCallback(parsedNotification).then()
                }, rejectCallback,
                () => {
                    snoozeCallback(parsedNotification).then()
                },
            )
            faultMetric.count(0)
            return notification
        })
        .catch((exception) => {
            faultMetric.count(1)
            console.error('ATBase: HeliosInterruptService: Error assigning labor move', exception)

            if (suppressAlerts) {
                console.warn('ATBase: HeliosInterruptService: updateLaborMove called by associate tapping snoozed banner failed with exception. Associate should be alerted.')
                return AtAlertService.alert(messages.error, '')
            } else {
                console.warn('ATBase: HeliosInterruptService: updateLaborMove called by system searching interrupt notification failed with exception. Associate shouldn\'t be alerted.')
                return null
            }
        })
}

/**
 * Takes a notification and parses the JSON metadata field.
 * In the case that notification has already been parsed, first check for valid fields before attempting to parse,
 * to prevent from logging error on a double-parse.
 *
 * @param notification - notification to be parsed
 */
const parseNotification = (notification: any) => {
    const parsed = _.cloneDeep(notification)
    if (isValidNotification(parsed)) {
        return parsed
    }

    try {
        parsed.metadata = JSON.parse(parsed.metadata)
        parsed.metadata.laborMoveId = notification.notificationId.split('|')[1]
        return parsed
    } catch (error) {
        console.error('ATBase: Failed to parse notification metadata', error)
    }
}

/**
 * Checks that a notification contains the valid fields for Helios.
 * For Helios notificaton, NotificationId is composed by Creator(i.e. Captain) + "|" + LaborMoveId.
 * Since we need to call hiveBeesServicesAbstraction.updateLaborMove with LaborMoveId frequently and hence we decorate laborMoveId into a parsed notification,
 * so we validate notificationId here.
 * A sample valid notification is:
 * { "applicableTool": "fetch_pick",
 *      "autoCloseAt": "2019-11-07T23:19:13Z",
 *         "body": "A labor move has been requested.",
 *         "metadata": "{\"warehouseId\":\"UIL1\",\"redirectZone\":\"BOH\",\"redirectSubZone\":\"Stow\"}",
 *         "notificationId": "Captain|bc4b784e-d222-4b32-9c9c-82953b2b06fc",
 *         "notificationType": "HELIOS",
 *         "rollUpNotification": false,
 *         "sequencer": 1,
 *         "status": "OPEN",
 *         "storeId": "LAB05",
 *         "targets": ["kejiawan"],
 *         "title": "Requested labor move",
 *         "updatedAt": "2019-11-07T22:49:02.538Z"},
 *
 * @param notification - notification to be validated
 */
const isValidNotification = (notification: any) => {
    return notification && notification.metadata && notification.metadata.warehouseId && notification.metadata.redirectZone &&
        notification.metadata.redirectSubZone && notification.applicableTool && notification.notificationId && notification.notificationId.split('|').length === 2
}

/**
 * Recursive function to do null check for and get property especially for deeply nested property for notification object
 * e.g. to get notification.metadata.warehouseId, you can call getNotificationProperty(notification, ["metadata", "warehouseId"])
 * @param notification - notification object that args belongs to
 * @param args - wanted property
 * @returns {*} - wanted property value
 */
const getNotificationProperty = (notification: any, args: any): any => {
    if (args.length === 0) {
        return notification
    }
    // if a property is undefined, then return null so that any caller for this property can catch a null parameter exception.
    if (!_.isObject(notification)) {
        return null
    }
    const arg = args.shift()
    return getNotificationProperty(notification[arg], args)
}

/**
 * Emits event for AssociateAudit
 *
 * @param {string} laborMoveId - the ID for the labor move that was updated
 * @param {string} action - the action that associate performed on the labor move (HeliosInterruptNotificationStates)
 */
const emitLaborMoveAuditEvent = (laborMoveId: string, action: string) => {
    Promise.resolve({})
        .then(() => {
            const auditEvent = AssociateAuditTrailService.generateBaseEvent(HeliosInterruptNotificationConstants.PROCESS_NAME)
            auditEvent.RequestId = laborMoveId
            auditEvent.Process = action
            AssociateAuditTrailService.recordEventAndEmitMetric(EnvironmentDetail.getStoreId(), auditEvent, HeliosInterruptNotificationConstants.PROCESS_NAME)
        })
}

/**
 * Check if the device is already in desitnation tool
 *
 * @param destinationTool - notification.applicableTool which is the destination tool that the page should be navigated to.
 * @returns {boolean}
 */
function isAlreadyInDestinationTool (destinationTool: string) {
    const currentTool: string = getSelectedTool(store.getState()).toolName
    return currentTool === destinationTool
}

/**
 * Parse notification into associate friendly message
 *
 * @param notification - notification.applicableTool need to be matched as what exists in IhmAssociateToolRegistrations key sets.
 * @returns {string}
 */
const constructInterruptMessage = (notification: any) => {
    if (!getToolById(notification.applicableTool)) {
        console.error('ATBase: no tool found with toolId: ' + notification.applicableTool)
        return null
    }
    const toolDisplayName: string = getToolById(notification.applicableTool).displayName
    const redirectZone = getNotificationProperty(notification, ['metadata', 'redirectZone'])
    const redirectSubZone = getNotificationProperty(notification, ['metadata', 'redirectSubZone'])
    if (!toolDisplayName || !redirectZone || !redirectSubZone) {
        console.error('ATBase: HeliosInterrupService: could not construct interrupt message, required fields are empty')
        return null
    }
    return messages.instructions1 + redirectZone + ' - ' + redirectSubZone + messages.instructions2 + toolDisplayName
}

/**
 * Acceptance workflow for a Helios labor move. Attempts to ACCEPT the labor move, if successful will redirect associate to zone/subzone/tool from notification data,
 * if unsuccessful will display error message and close modal.
 *
 * @param {object} notification - the notification associated with the interrupt, contains relevant labor move information
 */
const yesCallback = (notification: any) => {
    heliosModalResponseLatencyMetric.stop()
    return HiveServiceClient.getInstance()
    .updateLaborMove(EnvironmentDetail.getStoreId(), notification.metadata.warehouseId, notification.metadata.laborMoveId, HeliosInterruptNotificationStates.ACCEPTED)
        .then((response) => {
            if (!response.successful) {
                console.warn('ATBase: updateLaborMove to ACCEPTED failed, show alert.')
                return AtAlertService.alert(messages.notificationExpired, '')
            }
            return setCurrentToolHelper(notification.applicableTool)
        })
        .then(() => {
            emitLaborMoveAuditEvent(notification.metadata.laborMoveId, HeliosInterruptNotificationStates.ACCEPTED)
        })
        .catch((exception) => {
            console.error('ATBase: Failed to accept labor move, display alert.', exception)
            return AtAlertService.alert(messages.error, '')
        })
}

/**
 * Snooze workflow for a Helios labor move. Attempts to SNOOZE the labor move, if successful will set snoozedNotification variable, which causes banner to display,
 * and sets $timeout to retrigger the same notification after a period of time. If unsuccessful will display error message and close modal.
 * @param {object} notification - the notification associated with the interrupt, contains relevant labor move information
 */
const snoozeCallback = (notification: any) => {
    heliosModalResponseLatencyMetric.stop()
    return HiveServiceClient.getInstance()
    .updateLaborMove(EnvironmentDetail.getStoreId(), getNotificationProperty(notification, ['metadata', 'warehouseId']), getNotificationProperty(notification, ['metadata', 'laborMoveId']), HeliosInterruptNotificationStates.SNOOZED)
        .then((response) => {
            if (!response.successful) {
                console.warn('ATBase: updateLaborMove to SNOOZED state failed, show alert')
                return AtAlertService.alert(messages.unableToWait, '')
            }
            snoozedNotification = notification
            snoozeTimer = setTimeout(() => {
                triggerInterruptNotification(notification, false)
            }, HeliosInterruptNotificationConstants.TIMEOUT_MS)
        })
        .then(() => {
            emitLaborMoveAuditEvent(getNotificationProperty(notification, ['metadata', 'laborMoveId']), HeliosInterruptNotificationStates.SNOOZED)
        })
        .catch((exception) => {
            console.error('ATBase: Failed to snooze labor move', exception)
            return AtAlertService.alert(messages.error, '')
        })
}

const setCurrentToolHelper = (toolId: string) => {
    console.log('ATBase: HeliosInterruptService start redirecting to tool: ', toolId)
    CrossFrameUtils.sendMessageToOtherFrame({
        frame: window,
        message: {
            method: 'setCurrentTool',
            data: {
                toolId,
            },
        },
    })
    .then(() => {
        console.info('ATBase: successfully redirected to tool: ' + toolId)
    })
    .catch((errorResponse) => {
        console.error('ATBase: unable to redirect to specified tool. Error:' + errorResponse)
    })
}
